import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-form-field.css?inline';

/**
 * `<pk-form-field>` — a vertical label → input → hint → error field.
 * See `docs/components/pk-form-field.md`.
 *
 * Attributes:
 *   - `name`         (required) — name forwarded to the inner `<input>`
 *   - `label`        (required) — visible label text
 *   - `type`                   — input type (default `text`)
 *   - `value`                  — current value
 *   - `placeholder`            — placeholder text
 *   - `autocomplete`           — autocomplete hint
 *   - `required`               — marks the field required
 *   - `hint`                   — helper text below the input
 *   - `error`                  — inline error message (shows beneath hint, replaces hint)
 *   - `code`                   — verification-code variant: center-aligned, monospaced, letter-spaced
 *
 * Events (bubbling, composed):
 *   - `input`  — forwarded from the inner `<input>`
 *   - `change` — forwarded from the inner `<input>`
 */
export class PkFormField extends HTMLElement {
    static readonly tagName = 'pk-form-field';

    // Form-associated custom element. Required so the inner `<input>` (which
    // lives in pk-form-field's shadow DOM) participates in the surrounding
    // `<form>`'s submit data set. Without this, a form submit posts with no
    // fields — the inner inputs are shadow-scoped and the browser cannot
    // see them. With internals.setFormValue, the host element itself
    // contributes to the form's FormData under the host's `name` attribute.
    static readonly formAssociated = true;

    static get observedAttributes(): string[] {
        return [
            'name', 'label', 'type', 'value', 'placeholder',
            'autocomplete', 'required', 'hint', 'error', 'code',
        ];
    }

    private readonly _root: ShadowRoot;
    private readonly _internals: ElementInternals;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
        this._internals = this.attachInternals();
    }

    connectedCallback(): void {
        this.render();
        this._root.addEventListener('input', this._forwardEvent.bind(this, 'input'));
        this._root.addEventListener('change', this._forwardEvent.bind(this, 'change'));
        // Seed the form value from the initial `value` attribute (echoed back
        // from a server-rendered prefill) so a no-edit submit posts the right
        // value rather than empty string.
        this._setFormValue(this.getAttribute('value') ?? '');
    }

    attributeChangedCallback(name: string): void {
        // If a parent component or Razor template updates `value`, mirror it
        // into the form's data set so the next submit sees the new value.
        if (name === 'value') {
            this._setFormValue(this.getAttribute('value') ?? '');
        }
        this.render();
    }

    /**
     * setFormValue feature-detect. jsdom (used by vitest) ships
     * ElementInternals but does not implement setFormValue; real browsers do.
     * Without this guard, unit tests throw "setFormValue is not a function"
     * at component construction time.
     */
    private _setFormValue(value: string): void {
        if (typeof this._internals.setFormValue === 'function') {
            this._internals.setFormValue(value);
        }
    }

    private _forwardEvent(type: string, e: Event): void {
        const inner = e.target as HTMLInputElement;
        if (inner.tagName !== 'INPUT') return;

        // Stop the native event from escaping the shadow boundary on its own —
        // we re-dispatch a clean CustomEvent from the host so consumers see one event.
        e.stopImmediatePropagation();

        // Keep the host's value attribute in sync for easy programmatic reading.
        this.setAttribute('value', inner.value);
        // Update the form-associated value so the next submit picks it up.
        this._setFormValue(inner.value);

        this.dispatchEvent(
            new CustomEvent(type, {
                bubbles: true,
                composed: true,
                detail: { value: inner.value, name: inner.name },
            }),
        );
    }

    private get _inputId(): string {
        return `pk-ff-${this.getAttribute('name') ?? 'field'}`;
    }

    private get _hintId(): string {
        return `${this._inputId}-hint`;
    }

    private get _errorId(): string {
        return `${this._inputId}-error`;
    }

    private template(): TemplateResult {
        const name        = this.getAttribute('name') ?? '';
        const label       = this.getAttribute('label') ?? '';
        const type        = this.getAttribute('type') ?? 'text';
        const value       = this.getAttribute('value') ?? '';
        const placeholder = this.getAttribute('placeholder') ?? '';
        const autocomplete = this.getAttribute('autocomplete') ?? '';
        const isRequired  = this.hasAttribute('required');
        const hint        = this.getAttribute('hint') ?? '';
        const error       = this.getAttribute('error') ?? '';
        const isCode      = this.hasAttribute('code');

        const inputId = this._inputId;
        const hintId  = this._hintId;
        const errorId = this._errorId;

        const describedBy = [
            hint  ? hintId  : '',
            error ? errorId : '',
        ].filter(Boolean).join(' ');

        return html`
            <style>${styles}</style>
            <div class="field${isCode ? ' field--code' : ''}${error ? ' field--invalid' : ''}">
                <label class="field__label" for="${inputId}">
                    ${label}${isRequired ? html`<span class="field__required" aria-hidden="true"> *</span>` : ''}
                </label>
                <input
                    class="field__input"
                    id="${inputId}"
                    name="${name}"
                    type="${type}"
                    .value="${value}"
                    placeholder="${placeholder}"
                    autocomplete="${autocomplete}"
                    ?required="${isRequired}"
                    aria-describedby="${describedBy || ''}"
                    aria-invalid="${error ? 'true' : 'false'}"
                />
                ${hint && !error ? html`<p class="field__hint" id="${hintId}">${hint}</p>` : ''}
                ${error ? html`<p class="field__error" id="${errorId}" role="alert">${error}</p>` : ''}
            </div>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkFormField.tagName)) {
    customElements.define(PkFormField.tagName, PkFormField);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-form-field': PkFormField;
    }
}
